import { GuestLiteModel, useGetGuests } from 'api-hooks/guest';
import { KIND, Tag } from 'baseui/tag';
import { NavigationRoutes } from 'common/constants';
import notification from 'components/common/notification';
import QueryFilter from 'components/common/query-filter/query-filter';
import ListHeader from 'components/elements/list-header';
import { IColumn } from 'components/elements/table-component/table-component';
import { useApplyQueryFilter } from 'hooks/filter/use-apply-query-filter';
import { FilterHandleParentProvider } from 'hooks/filter/use-filter-handle-parent';
import useApplyQuerySort from 'hooks/use-apply-query-sort';
import useComposedQuery from 'hooks/use-composed-query';
import useGetCrudPermission from 'hooks/use-get-crud-permission';
import useGetListParamsValue from 'hooks/use-get-list-params-value';
import useNavigation from 'hooks/use-navigation';
import { Container, TopContainer } from 'legacy-modules/styles';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';

import ListHeaderActionComponent from './components/list-header-action-component';
import GuestTable from './components/table';

export default function GuestList() {
  const { t } = useTranslation();
  const { queryPage } = useGetListParamsValue();
  const [page, setPage] = React.useState(queryPage);
  const navigation = useNavigation();

  const onCreate = React.useCallback(() => {
    navigation.navigate(NavigationRoutes.GuestsCreate);
  }, [navigation]);

  const _columns = React.useMemo<IColumn<GuestLiteModel>[]>(
    () => [
      {
        header: t('common:name'),
        accessorKey: 'name',
        minSize: 250,
        sortName: 'name',
        cell: ({ row }) => {
          const { original } = row;
          return `${t(`enum:${original.title}`)} ${original.name}`;
        },
      },
      {
        header: t('common:alias'),
        accessorKey: 'alias',
        minSize: 250,
        sortName: 'alias',
      },
      {
        header: t('common:category'),
        accessorKey: 'category',
        minSize: 80,
        sortName: 'category',
        cell: ({ getValue }) => (getValue() as string).toUpperCase(),
      },
      {
        header: t('common:email'),
        accessorKey: 'email',
        minSize: 180,
      },
      {
        header: t('cruise:tel_phone'),
        accessorKey: 'phone',
        minSize: 180,
      },
      {
        header: t('cruise:cellphone_number'),
        accessorKey: 'phone_2',
        minSize: 180,
      },
      {
        header: t('common:guest_preferences'),
        accessorKey: 'preferences',
        minSize: 250,
        cell: ({ getValue }) => {
          const preferences = getValue() as any;
          if (!preferences.length) {
            return <>-</>;
          }
          return (
            <>
              {(getValue() as any).map((item) => (
                <Tag key={item.id} closeable={false} kind={KIND.blue}>
                  {item.name}
                </Tag>
              ))}
            </>
          );
        },
      },
      {
        header: t('common:preferences'),
        accessorKey: 'preferenceStats',
        minSize: 250,
        cell: ({ getValue }) => {
          const preferenceStats = getValue() as any;
          if (!preferenceStats.length) {
            return <>-</>;
          }
          return (
            <>
              {(getValue() as any).map((item) => (
                <Tag key={item.id} closeable={false} kind={KIND.blue}>{`${
                  item.name
                } ${item.count > 1 ? `(X${item.count})` : ''}`}</Tag>
              ))}
            </>
          );
        },
      },
      {
        header: t('common:created_by'),
        accessorKey: 'createdBy.name',
        minSize: 150,
        sortName: 'created_by',
        cell: ({ getValue }) => <>{getValue() as string}</>,
      },
    ],
    [t],
  );

  const {
    data,
    refetch,
    isLoading,
    isFetching,
    error,
    query,
    extras: [{ filters, setFilters }, { columns }],
  } = useComposedQuery(
    useGetGuests,
    {
      params: {
        page,
      },
    },
    useApplyQueryFilter((data: any) => {
      return data.filters;
    }),
    useApplyQuerySort((data: any) => {
      return data.sorts;
    }, _columns),
  );

  React.useEffect(() => {
    if (!!error && !!error.message) {
      notification.error({ message: error?.message });
    }
  }, [error]);
  const permission = useGetCrudPermission('guests');
  return (
    <FilterHandleParentProvider
      values={{
        filters,
        setFilters,
        page,
        setPage,
      }}
    >
      <Container>
        <ListHeader
          query={query}
          title={t('common:guest')}
          showAdd={false}
          showRefresh={false}
          rightCustomComponent={ListHeaderActionComponent({
            query,
            onCreate,
            onRefresh: refetch,
            showAdd: permission.create,
          })}
        />
        <TopContainer>
          <QueryFilter filters={filters} isUseResponseOptions />
        </TopContainer>
        <GuestTable
          guests={data?.data || []}
          loading={isLoading || isFetching}
          page={page}
          onPageChange={(page) => setPage(page)}
          meta={data?.meta}
          sortColumns={columns || []}
        />
      </Container>
    </FilterHandleParentProvider>
  );
}
